import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Check, X } from "lucide-react";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("timeout");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 15; i++) {
        if (cancelled) return;
        try {
          const res = await api.get(`/subscription/status/${sessionId}`);
          setAttempts(i + 1);
          if (res.data.payment_status === "paid") {
            setStatus("paid");
            return;
          }
        } catch (err) {
          // 404 or transient — keep trying
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center px-6 pt-16">
      <div className="max-w-lg w-full text-center">
        {status === "polling" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto mb-8" />
            <h1 className="font-serif-display text-4xl text-stone-100 mb-4">
              Just <span className="italic text-orange-600">plating</span> your pass...
            </h1>
            <p className="text-sm text-stone-400">
              Confirming with Stripe. Won&apos;t be a moment. (Attempt {attempts}/15)
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 border border-lime-500 mb-8 mx-auto">
              <Check className="w-8 h-8 text-lime-500" />
            </div>
            <h1
              data-testid="payment-success-title"
              className="font-serif-display text-4xl sm:text-5xl text-stone-100 mb-4"
            >
              You&apos;re <span className="italic text-orange-600">in</span>.
            </h1>
            <p className="text-stone-300 mb-10">
              Kitchen Pass activated. Time to cook something showy.
            </p>
            <Link
              to="/generate"
              data-testid="payment-success-cta"
              className="inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-8 py-4 uppercase text-xs tracking-[0.24em] font-medium transition-colors"
            >
              Off to the pass →
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 border border-orange-500 mb-8 mx-auto">
              <X className="w-8 h-8 text-orange-500" />
            </div>
            <h1 className="font-serif-display text-4xl text-stone-100 mb-4">
              Payment still processing
            </h1>
            <p className="text-stone-400 mb-8">
              Stripe hasn&apos;t confirmed yet. Your pass will activate automatically once it does —
              try refreshing this page in a minute or head to your account.
            </p>
            <button
              onClick={() => navigate("/subscribe")}
              className="text-orange-500 hover:text-orange-400 uppercase text-xs tracking-[0.24em] ink-link"
            >
              Back to plans
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
